import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import PageHero from '@/components/ui/PageHero'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import type { Theme } from '@/types'
import * as api from '@/lib/api'

export default function ThemesPage() {
  // SEO
  useSEO({
    title: 'Explore by Theme',
    description: 'Cross-archive themes connecting treaties, conflicts, civilizations, company origins, lineages, classified secrets, explorations, and mythology. Discover interconnected historical narratives.',
    url: 'https://thearchivenetwork.com/themes',
  })
  const [loading, setLoading] = useState(true)
  const [themes, setThemes] = useState<Theme[]>([])

  useEffect(() => {
    async function loadData() {
      try {
        const data = await api.getThemes()
        setThemes(data)
      } catch (error) {
        console.error('Failed to load themes:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      <PageHero
        image="/images/covers/themes.jpg"
        title="Explore by Theme"
        subtitle="Cross-archive themes connecting our growing network of archives. Discover interconnected historical narratives."
        variant="full"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        {/* Themes Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {themes.map((theme) => (
            <Link
              key={theme.slug}
              to={`/themes/${theme.slug}`}
              className="group artifact-frame overflow-hidden"
            >
              {/* Hero image */}
              <div className="aspect-[2/1] bg-archive-slate overflow-hidden">
                {theme.hero_image_url ? (
                  <img
                    src={theme.hero_image_url}
                    alt={theme.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-archive-slate to-archive-charcoal">
                    <span className="text-5xl text-archive-ash font-display">
                      {theme.title[0]}
                    </span>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h2 className="font-display text-xl text-archive-cream group-hover:text-archive-gold transition-colors">
                  {theme.title}
                </h2>
                <p className="mt-2 text-archive-muted line-clamp-3">
                  {theme.description}
                </p>
                <span className="inline-flex items-center gap-2 mt-4 text-sm text-archive-gold">
                  Explore theme
                  <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </div>
            </Link>
          ))}
        </div>

        {themes.length === 0 && (
          <div className="text-center py-12 text-archive-muted">
            No themes available yet.
          </div>
        )}
      </div>
    </div>
  )
}
